import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { lookupByPostalCode, type ParsedLocationData } from '../api/services/here/here.service';

type UsePostalCodeLookupOptions = {
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** ISO country code to restrict results (e.g., 'USA', 'DEU') */
  countryCode?: string;
  /** Whether the lookup is enabled */
  enabled?: boolean;
};

type UsePostalCodeLookupReturn = {
  /** Parsed location data from postal code lookup */
  locationData: ParsedLocationData | null;
  /** Whether the lookup is in progress */
  isLoading: boolean;
  /** Error message if lookup failed */
  error: string | null;
  /** Manually trigger a lookup */
  lookup: (postalCode: string) => void;
  /** Clear the location data */
  clear: () => void;
};

/**
 * Hook to lookup city/region/country from postal code using HERE Maps API
 * @param postalCode - The postal code to lookup
 * @param options - Configuration options
 * @returns Location data and lookup state
 * @example
 * ```tsx
 * const { locationData, isLoading } = usePostalCodeLookup(zipCode, {
 *   debounceMs: 500,
 *   countryCode: 'USA',
 * });
 *
 * useEffect(() => {
 *   if (locationData?.city) {
 *     setValue('city', locationData.city);
 *   }
 * }, [locationData]);
 * ```
 */
export const usePostalCodeLookup = (
  postalCode: string,
  options: UsePostalCodeLookupOptions = {}
): UsePostalCodeLookupReturn => {
  const { debounceMs = 500, countryCode, enabled = true } = options;

  const [debouncedPostalCode, setDebouncedPostalCode] = useState('');
  const [manualLookup, setManualLookup] = useState<string | null>(null);

  // Debounce postal code changes
  useEffect(() => {
    // Always update debounced value, let the query handle enabled state
    const timer = setTimeout(() => {
      setDebouncedPostalCode(postalCode);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [postalCode, debounceMs]);

  // Determine the code to lookup
  const codeToLookup = manualLookup || debouncedPostalCode;
  const shouldQuery = enabled && codeToLookup.trim().length >= 3;

  // Query for postal code lookup
  const {
    data: locationData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['postalCodeLookup', codeToLookup, countryCode],
    queryFn: () => lookupByPostalCode(codeToLookup, countryCode),
    enabled: shouldQuery,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    retry: 1,
  });

  // Manual lookup trigger
  const lookup = useCallback((code: string) => {
    setManualLookup(code);
  }, []);

  // Clear location data
  const clear = useCallback(() => {
    setManualLookup(null);
    setDebouncedPostalCode('');
  }, []);

  // Reset manual lookup after successful query
  useEffect(() => {
    if (manualLookup && locationData) {
      setManualLookup(null);
    }
  }, [manualLookup, locationData]);

  return {
    locationData: locationData || null,
    isLoading,
    error: error ? 'Failed to lookup postal code' : null,
    lookup,
    clear,
  };
};

export default usePostalCodeLookup;
